/**
 * API Helper Service for Website eCommerce
 * Provides fetch wrappers with multi-tenant support
 */

import { getTenantFromSubdomain } from './tenant';
import { API_URL, getApiUrl } from './apiConfig';
import {
  forceSessionExpired,
  isSessionRedirectInProgress,
  reasonFromAuthError,
} from './sessionExpiry';

const TOKEN_KEY = 'ecommerce_token';

/**
 * Endpoints that legitimately return 401 as part of their normal flow
 * (login, registration). A 401 from these is NOT a stale-session signal,
 * so we must not redirect or clear the token on them — that would interrupt
 * the very flow the customer is trying to complete.
 */
const isAuthEndpoint = (endpoint = '') =>
  /\/(login|register|signup|password|auth)\b/i.test(endpoint);

/**
 * Get default headers including tenant header
 */
export const getHeaders = (token = null) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  const tenant = getTenantFromSubdomain();
  if (tenant) {
    headers['X-Tenant-ID'] = tenant;
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * API fetch wrapper with tenant support.
 *
 * @param {string} endpoint
 * @param {object} options - fetch options. Additionally supports:
 *   options.skipAuthRedirect {boolean} - if true, a 401 is returned to the
 *     caller as-is instead of triggering the stale-session redirect. Use for
 *     calls where the caller wants to handle 401 itself.
 */
export const apiFetch = async (endpoint, options = {}) => {
  const { skipAuthRedirect = false, ...fetchOptions } = options;
  const token = localStorage.getItem(TOKEN_KEY);

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...fetchOptions,
    headers: {
      ...getHeaders(token),
      ...fetchOptions.headers,
    },
  });

  // Stale-session handling: only when we actually sent a token, the call
  // isn't an auth endpoint, and the caller hasn't opted out.
  if (
    response.status === 401 &&
    token &&
    !skipAuthRedirect &&
    !isAuthEndpoint(endpoint) &&
    !isSessionRedirectInProgress()
  ) {
    let message;
    try {
      const body = await response.clone().json();
      message = body?.message;
    } catch (_) {
      /* ignore */
    }
    forceSessionExpired(
      reasonFromAuthError({ status: 401, message }),
      message
    );
  }

  return response;
};

/**
 * Convenience methods
 */
export const api = {
  get: (endpoint, options) => apiFetch(endpoint, { ...options, method: 'GET' }),

  post: (endpoint, data, options) => apiFetch(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  }),

  put: (endpoint, data, options) => apiFetch(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  patch: (endpoint, data, options) => apiFetch(endpoint, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  delete: (endpoint, options) => apiFetch(endpoint, { ...options, method: 'DELETE' }),
};

export { getTenantFromSubdomain, getApiUrl, API_URL };
export default api;
